import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/pagination";
import "./ProductTheme.css";   

import {
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaTwitter,
  FaLinkedin,
  FaTiktok,
  FaSnapchat,
  FaPinterest,
  FaWhatsapp,
  FaTelegram,
} from "react-icons/fa";

import Thumbnail from "../../../assests/solutionImages/productLanding/youtube-thumbnail.svg";

const ProductLandingTheme = ({ card }) => {

  // Use card data if available, otherwise fallback to static data
  const cardData = card?.data || {};
  
  // Helper function to get social media icon
  const getSocialIcon = (platform) => {
    const iconMap = {
      facebook: FaFacebook,
      instagram: FaInstagram,
      youtube: FaYoutube,
      twitter: FaTwitter,
      linkedin: FaLinkedin,
      tiktok: FaTiktok,
      snapchat: FaSnapchat,
      pinterest: FaPinterest,
      whatsapp: FaWhatsapp,
      telegram: FaTelegram,
    };
    return iconMap[platform] || FaFacebook;
  };

  // Helper function to get social media colors
  const getSocialColor = (platform) => {
    const colorMap = {
      facebook: "text-blue-600",
      instagram: "text-pink-500",
      youtube: "text-red-600",
      twitter: "text-blue-400",
      linkedin: "text-blue-700",
      tiktok: "text-black",
      snapchat: "text-yellow-400",
      pinterest: "text-red-500",
      whatsapp: "text-green-500",
      telegram: "text-blue-500",
    };
    return colorMap[platform] || "text-blue-600";
  };

  // Prepare slider content
  const sliderContent = cardData.basicInfo?.slidePhotos?.length > 0 
    ? cardData.basicInfo.slidePhotos.map((slide, index) => ({
        id: index + 1,
        title: slide.text1 || `Title ${index + 1}`,
        heroImage: slide.image ,
        logo: card?.profileImage ,
        link: slide.link || "#"
      }))
    : [];

  // Prepare images links data
  const imagesLinksData = cardData.imagesLinks?.photos?.length > 0
    ? cardData.imagesLinks.photos.map(photo => ({
        fileList: [{ url: photo.image }],
        text1: photo.text1 || "",
        link: photo.link || "#"
      }))
    : [];

  // Prepare products data
  const productsData = cardData.products?.items?.length > 0
    ? cardData.products.items.map(product => ({
        imageLink: product.imageLink, // <-- ensure this is mapped
        fileList: [{ url: product.image }], // fallback if imageLink is missing
        productName: product.productName || "Product Name",
        price: product.price || "10.50",
        rating: product.rating || 4,
        ratingEnabled: product.ratingEnabled || true,
        buttonLabel: product.buttonLabel || "Buy Online",
        link: product.link || "#",
      }))
    : [];

  // Prepare social links data
  const socialLinksData = cardData.socialLinks?.links?.length > 0
    ? cardData.socialLinks.links
    : [];

  const handleVideoClick = () => {
    const videoLink = cardData.video?.link || "https://www.youtube.com/";
    window.open(videoLink, "_blank");
  };

  const handleLinkClick = (link) => {
    if (link && link !== "#") window.open(link, "_blank");
  };

  const renderStars = (rating) => {
    const stars = "★★★★★";
    const emptyStars = "☆☆☆☆☆";
    return stars.slice(0, rating) + emptyStars.slice(rating);
  };

  // Add default images if needed for fallback
  const defaultShoes = [
    // shoe1, shoe2, shoe3
  ];

  // Helper for imagesLinksData with fallback
  const getImagesLinksData = () => imagesLinksData;

  return (
    <div className="flex p-2 min-h-screen md:py-4 justify-center bg-gray-200">
      <div className="text-white min-h-screen bg-black max-w-[393px] rounded-lg overflow-hidden font-poppins">
        {/* Hero Slider Section */}
        <div className="relative w-full h-[80vh] overflow-hidden">
          <Swiper
            modules={[Autoplay, Pagination]}
            slidesPerView={1}
            spaceBetween={10}
            loop={true}
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            className="w-full h-full"
          >
            {sliderContent.map((slide) => (
              <SwiperSlide key={slide.id}>
                <div className="relative w-full h-[80vh]">
                  <img
                    src={slide.heroImage}
                    alt="Background"
                    className="w-full h-full object-cover"
                  />
                  {cardData.basicInfo?.brandLogoEnabled && (
                    <img
                      src={slide.logo}
                      alt="Logo"
                      className="w-14 absolute top-14 left-8 z-10"
                    />
                  )}
                  <h1 className="text-white text-center absolute bottom-10 left-1/2 transform -translate-x-1/2 text-2xl font-bold bg-black bg-opacity-50 px-4 py-2 rounded z-10">
                    {slide.title}
                  </h1>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="p-2">
          {/* Main Description Section */}
          <div className="my-2 text-center rounded-xl p-4 ">
            <h2 className="text-xl font-semibold text-white">
              {cardData.basicInfo?.heading || "Heading"}
            </h2>
            <p className="text-gray-400 mt-1">
              {cardData.basicInfo?.description || "Description"}
            </p>
          </div>
          
          {/* Heading + Text Section */}
          {cardData.headingText?.enabled && (
            <div className="my-2 text-center rounded-xl p-4 bg-slate-100">
              <h2 className="text-xl font-semibold text-custom-gradient">
                {cardData.headingText.title || "About"}
              </h2>
              <p className="text-gray-400 mt-1">
                {cardData.headingText.description || "Description"}
              </p>
            </div>
          )}

          {/* Images + Links Section */}
          {cardData.imagesLinks?.enabled && (
            <div className="bg-[#EEEEEE] p-2 rounded-xl py-3">
              <Swiper
                modules={[Autoplay]}
                spaceBetween={10}
                slidesPerView={3}
                autoplay={{
                  delay: 2500,
                  disableOnInteraction: false,
                }}
                loop={true}
              >
                {getImagesLinksData().map((img, idx) => (
                  <SwiperSlide key={idx}>
                    <div
                      className=" rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => handleLinkClick(img.link)}
                    >
                      <img
                        src={img.fileList?.[0]?.url || img.fileList?.[0]?.thumbUrl || defaultShoes[idx % defaultShoes.length]}
                        alt={`item-${idx}`}
                        className="w-20 h-20 object-cover mx-auto" 
                      />
                      {/* {img.text1 && (
                        <p className="text-center text-red-500 text-sm mt-2 break-words">{img.text1}</p>
                      )} */}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {/* Video Section */}
          {cardData.video?.enabled && (
            <div className="bg-slate-100 text-black rounded-xl my-2 p-4">
              {cardData.video.titleDescEnabled && (
                <div className="my-2 text-center">
                  <h3 className="text-xl font-semibold text-custom-gradient">
                    {cardData.video.title || "Video"}
                  </h3>
                  <p className="text-gray-400 mt-1">
                    {cardData.video.description || "Description"}
                  </p>
                </div>
              )}
              <div className="relative cursor-pointer" onClick={handleVideoClick}>
                <img
                  src={Thumbnail}
                  alt="Video Thumbnail"
                  className="rounded-xl w-full opacity-75 h-[175px]"
                />
                <button className="absolute inset-0 flex justify-center items-center">
                  <FaYoutube className="w-12 h-12 text-red-600" />
                </button>
              </div>
            </div>
          )}

          {/* Products Section */}
          {cardData.products?.enabled && (
            <div className="bg-slate-100 rounded-xl my-2 px-4">
              {cardData.products.titleDescEnabled && (
                <div className="my-2 text-center">
                  <h2 className="text-xl font-semibold text-custom-gradient">
                    {cardData.products.title || "Products"}
                  </h2>
                  <p className="text-gray-400 mb-4">
                    {cardData.products.description || "Description"}
                  </p>
                </div>
              )}
              <Swiper
                modules={[Autoplay]}
                slidesPerView={2}
                autoplay={{
                  delay: 3500,
                  disableOnInteraction: false,
                }}
              >
                {productsData.map((product, idx) => (
                  <SwiperSlide key={idx}>
                    <div className="bg-white text-black text-[10px] p-2 rounded-xl text-center m-1">
                      <img
                        src={
                          product.imageLink ||
                          "https://metatestbucket.s3.ap-south-1.amazonaws.com/419b841b0b9b092d79d47f3b2cd2b783f1adcc7d00aa65bb25b80d44ec57afcf"
                        }
                        alt={`product-${idx}`}
                        className="w-[94px] h-20 mx-auto"
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = "https://metatestbucket.s3.ap-south-1.amazonaws.com/419b841b0b9b092d79d47f3b2cd2b783f1adcc7d00aa65bb25b80d44ec57afcf";
                        }}
                      />
                      <p className="font-semibold mt-2">
                        {product.productName}
                      </p>
                      {product.ratingEnabled && (
                        <div className="flex justify-center items-center text-custom-gradient">
                          {renderStars(product.rating)}
                        </div>
                      )}
                      <p className="text-sm text-gray-600">
                        ${product.price}
                      </p>
                      <button
                        className="bg-custom-gradient text-white text-[10px] px-3 py-1 rounded mt-2"
                        onClick={() => handleLinkClick(product.link)}
                      >
                        {product.buttonLabel}
                      </button>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {/* CTA 1 Section */}
          {cardData.callToAction?.enabled && (
            <div className="bg-white text-black rounded-xl my-2 p-4 text-center">
              <h3 className="font-bold text-lg text-custom-gradient">
                {cardData.callToAction.title || "Call to Action"}
              </h3>
              <p className="mt-1 mb-4 text-gray-400">
                {cardData.callToAction.description || "Description"}
              </p>
              <button
                className="bg-custom-gradient text-white px-6 py-2 rounded-full"
                onClick={() => handleLinkClick(cardData.callToAction.link)}
              >
                {cardData.callToAction.buttonLabel || "Click Here"}
              </button>
            </div>
          )}

          {/* CTA 2 Section */}
          {cardData.callToAction2?.enabled && (
            <div className="bg-white text-black rounded-xl my-2 p-4 text-center relative overflow-hidden">
              <div className="absolute inset-0 z-0">
                <img
                  src={cardData.callToAction2.image }
                  alt="CTA Background"
                  className="w-full h-full object-cover opacity-20"
                />
              </div>
              <div className="relative z-10">
                <h3 className="font-bold text-lg text-custom-gradient">
                  {cardData.callToAction2.title || "Special Offer"}
                </h3>
                <p className="mt-1 mb-4 text-gray-400">
                  {cardData.callToAction2.description || "Description"}
                </p>
                <button
                  className="bg-custom-gradient text-white px-6 py-2 rounded-full"
                  onClick={() => handleLinkClick(cardData.callToAction2.link)}
                >
                  {cardData.callToAction2.buttonLabel || "Click Here"}
                </button>
              </div>
            </div>
          )}

          {/* Social Links Section */}
          {cardData.socialLinks?.enabled && (
            <div className="space-y-2">
              {socialLinksData.map((social, idx) => {
                const IconComponent = getSocialIcon(social.platform);
                const colorClass = getSocialColor(social.platform);
                
                return (
                  <div
                    key={idx}
                    className="flex items-center bg-white text-black p-3 rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleLinkClick(social.link)}
                  >
                    <IconComponent className={`${colorClass} text-2xl mr-3`} />
                    <div>
                      <p className="font-bold text-sm">{social.title}</p>
                      {social.subtitleEnabled && (
                        <p className="text-xs text-gray-600">{social.subtitle}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductLandingTheme;
